import CreatorPackageAccountEditor, {
  type CreatorPackageAccountEditorProps
} from './CreatorPackageAccountEditor';
import { useId } from 'react';
import CreatorPackageRatePanel, {
  CreatorPackageRateError
} from './CreatorPackageRatePanel';
import CreatorPackageRateSection, {
  type CreatorPackageRateSectionProps
} from './CreatorPackageRateSection';

export type CreatorPackageQuoteEditorProps = Omit<
  CreatorPackageAccountEditorProps,
  'footer' | 'footerError' | 'hidePlacementTotal' | 'showPackageRateError'
> &
  Omit<CreatorPackageRateSectionProps, 'hidePackageRatePanel'> & {
    /** Quotes with one selected channel use its channel rate directly. */
    singleChannelRateMode?: boolean;
  };

/**
 * Shared quote fields for initial quotes and counteroffers.
 * State, validation and API submission stay with the owning flow.
 * Package pricing joins the channel card only when multiple channels are selected.
 */
export default function CreatorPackageQuoteEditor({
  collaborationPackage,
  mobile = false,
  rates,
  includedPlacementIds,
  expandedPlacementIds,
  selectedContentTypes,
  channelRecommendations,
  initializeFromRecommendation,
  recommendationLoading,
  loadingPlacementIds,
  singleChannelRateMode = false,
  quoteTermsReadOnly = false,
  contentTypesReadOnly = false,
  accountsTitle,
  showAccountsTitle,
  showAccountSelector,
  accountNotice,
  accountSelectorDisabled,
  accountSelectorDisabledReason,
  accountSelectorOpen,
  onAccountSelectorOpenChange,
  allowRemove,
  showConflictValidation,
  invalidRatePlacementIds,
  onRatesChange,
  onIncludedPlacementIdsChange,
  onExpandedPlacementIdsChange,
  onSelectedContentTypesChange,
  ...rateSectionProps
}: CreatorPackageQuoteEditorProps) {
  const validationMessageId = useId();
  return (
    <>
      <CreatorPackageAccountEditor
        collaborationPackage={collaborationPackage}
        mobile={mobile}
        quoteTermsReadOnly={quoteTermsReadOnly}
        contentTypesReadOnly={contentTypesReadOnly}
        rates={rates}
        includedPlacementIds={includedPlacementIds}
        expandedPlacementIds={expandedPlacementIds}
        selectedContentTypes={selectedContentTypes}
        channelRecommendations={channelRecommendations}
        initializeFromRecommendation={initializeFromRecommendation}
        recommendationLoading={recommendationLoading}
        loadingPlacementIds={loadingPlacementIds}
        hidePlacementTotal={singleChannelRateMode}
        accountsTitle={accountsTitle}
        showAccountsTitle={showAccountsTitle}
        showAccountSelector={showAccountSelector}
        accountNotice={accountNotice}
        accountSelectorDisabled={accountSelectorDisabled}
        accountSelectorDisabledReason={accountSelectorDisabledReason}
        accountSelectorOpen={accountSelectorOpen}
        onAccountSelectorOpenChange={onAccountSelectorOpenChange}
        allowRemove={allowRemove}
        showConflictValidation={showConflictValidation}
        invalidRatePlacementIds={invalidRatePlacementIds}
        showPackageRateError={
          rateSectionProps.packageRateValidation?.isValid === false
        }
        onRatesChange={onRatesChange}
        onIncludedPlacementIdsChange={onIncludedPlacementIdsChange}
        onExpandedPlacementIdsChange={onExpandedPlacementIdsChange}
        onSelectedContentTypesChange={onSelectedContentTypesChange}
        footerError={
          singleChannelRateMode ? undefined : (
            <CreatorPackageRateError
              validation={rateSectionProps.packageRateValidation}
              id={validationMessageId}
            />
          )
        }
        footer={
          singleChannelRateMode ? undefined : (
            <CreatorPackageRatePanel
              mobile={mobile}
              quoteTermsReadOnly={quoteTermsReadOnly}
              placementTotal={rateSectionProps.placementTotal}
              packageRate={rateSectionProps.packageRate}
              recommendationRange={rateSectionProps.recommendationRange}
              packageRateValidation={rateSectionProps.packageRateValidation}
              validationMessageId={validationMessageId}
              hideValidationMessage
              onPackageRateChange={rateSectionProps.onPackageRateChange}
            />
          )
        }
      />
      <CreatorPackageRateSection
        {...rateSectionProps}
        collaborationPackage={collaborationPackage}
        mobile={mobile}
        quoteTermsReadOnly={quoteTermsReadOnly}
        includedPlacementIds={includedPlacementIds}
        showConflictValidation={showConflictValidation}
        hidePackageRatePanel
      />
    </>
  );
}
